import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import Icon from './Icon'

// Barra de carga rápida POS: input ancho + dropdown de resultados (↑↓/Enter) + cámara + Agregar.
// El dropdown se renderiza en un PORTAL (document.body) para que NO lo clipee el overflow de la card.
export default function ScanSearch({ fetcher, onSelect, placeholder, renderResult, minChars = 2 }) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const [loading, setLoading] = useState(false)
  const [rect, setRect] = useState(null)
  const timer = useRef(null)
  const fieldRef = useRef(null)

  useEffect(() => {
    if (q.length < minChars) { setResults([]); setOpen(false); return }
    setLoading(true)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(async () => {
      try {
        const r = await fetcher(q)
        const list = Array.isArray(r) ? r : r?.list || r?.data || []
        setResults(list.slice(0, 12)); setActive(0); setOpen(true)
      } catch { setResults([]) } finally { setLoading(false) }
    }, 300)
    return () => timer.current && clearTimeout(timer.current)
  }, [q]) // eslint-disable-line react-hooks/exhaustive-deps

  // Posición del dropdown (portal) bajo el input; recalcula al scrollear/resize.
  useEffect(() => {
    if (!open) return
    const upd = () => fieldRef.current && setRect(fieldRef.current.getBoundingClientRect())
    upd()
    window.addEventListener('scroll', upd, true)
    window.addEventListener('resize', upd)
    return () => { window.removeEventListener('scroll', upd, true); window.removeEventListener('resize', upd) }
  }, [open, results])

  useEffect(() => {
    const onDoc = (e) => { if (fieldRef.current && !fieldRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const choose = (item) => { if (!item) return; onSelect(item); setQ(''); setResults([]); setOpen(false) }
  const onKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); if (open && results[active]) choose(results[active]); return }
    if (!open || !results.length) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)) }
    else if (e.key === 'Escape') setOpen(false)
  }

  const dropdown = open && results.length > 0 && rect && createPortal(
    <div className="scan-dropdown" style={{ position: 'fixed', top: rect.bottom + 4, left: rect.left, width: rect.width, right: 'auto', zIndex: 1500 }}>
      <div className="scan-dropdown-head">{results.length} resultados · ↑↓ para navegar · Enter para agregar</div>
      {results.map((it, i) => (
        <div key={it.id} className={'scan-result' + (i === active ? ' active' : '')}
          onMouseEnter={() => setActive(i)} onMouseDown={(e) => { e.preventDefault(); choose(it) }}>
          {renderResult(it)}
        </div>
      ))}
    </div>,
    document.body,
  )

  return (
    <div className="scan-bar">
      <div className="scan-field" ref={fieldRef}>
        <span className="lead"><Icon name="search" size={20} stroke={1.8} /></span>
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
          onFocus={() => results.length && setOpen(true)} placeholder={placeholder} />
        <span className="hint"><span className="kbd">{loading ? '…' : 'F2'}</span></span>
      </div>
      <button className="scan-secondary" type="button" title="Escanear con cámara"><Icon name="qr" size={18} /></button>
      <button className="scan-add" type="button" disabled={!results[active]} onClick={() => choose(results[active])}>
        <Icon name="plus" size={16} /> Agregar
      </button>
      {dropdown}
    </div>
  )
}
